import type { FunctionComponent } from 'react';
import { Badge, Box, Group, Stack, Text, Tooltip } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { breakpoints } from 'styles/theme.variables';

import type { ValidationError } from 'components/MenuManagement/MenuEdit/Hours/types';
import { InfoCircleIconSm, WarningIconSm } from '@chownow/pantry/icons';

type ErrorCountBadgeProps = {
  validationErrors: ValidationError[];
  scheduleId: string;
  hasRollover: boolean;
};

const ROLLOVER_MESSAGE = 'End time rolls over to the next day.';

export const ErrorCountBadge: FunctionComponent<ErrorCountBadgeProps> = ({
  validationErrors,
  scheduleId,
  hasRollover,
}) => {
  const isMobile = useMediaQuery(`(max-width: ${breakpoints.maxMobile})`, undefined, {
    getInitialValueInEffect: false,
  });

  // Collect unique error messages from validations that match this schedule
  const errorMessages = [
    ...new Set(
      validationErrors
        .filter(error => error.periods.some(period => period.id === scheduleId))
        .flatMap(error => error.messages),
    ),
  ];

  const hasErrors = errorMessages.length > 0;
  const totalCount = errorMessages.length + (hasRollover ? 1 : 0);

  if (totalCount === 0) {
    if (isMobile) return <Box w={24} h={24} />;
    return null;
  }

  const tooltipContent = (
    <Stack gap="sm">
      {errorMessages.map(msg => (
        <Group key={msg} gap="xs2" wrap="nowrap" align="flex-start">
          <WarningIconSm style={{ flexShrink: 0 }} />
          <Text variant="sublabel-sm" c="var(--content-inverse-primary)">
            {msg}
          </Text>
        </Group>
      ))}
      {hasRollover && (
        <Group gap="xs2" wrap="nowrap" align="flex-start">
          <InfoCircleIconSm style={{ flexShrink: 0 }} />
          <Text variant="sublabel-sm" c="var(--content-inverse-primary)">
            {ROLLOVER_MESSAGE}
          </Text>
        </Group>
      )}
    </Stack>
  );

  return (
    <Tooltip
      label={tooltipContent}
      position="top-end"
      middlewares={{
        flip: { fallbackPlacements: ['top', 'bottom-end', 'bottom', 'right'] },
      }}
      events={{ hover: true, focus: true, touch: true }}
      multiline
      w={200}
      offset={8}
      withArrow
      arrowSize={8}
      arrowPosition="side"
      arrowOffset={12}
      bg="var(--surface-inverse-primary)"
      c="var(--content-inverse-primary)"
      radius="xs2"
      p="xs2"
      styles={{ tooltip: { maxWidth: 'calc(100vw - 32px)' } }}
    >
      <Badge
        size="xs"
        miw="md"
        h="sm"
        py={0}
        px="xs3"
        radius="xs"
        fz="xs"
        bg={hasErrors ? 'var(--content-caution)' : 'var(--surface-inverse-primary)'}
        c="var(--content-inverse-primary)"
        style={{ cursor: 'pointer' }}
      >
        {totalCount}
      </Badge>
    </Tooltip>
  );
};
